import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris";
import {
  DatePicker,
  DateInput,
  FormField,
  Calendar,
  CollectionPreferencesProps,
} from "@amzn/awsui-components-react";
import {
  commonPropertyFilterI18nStrings,
  pageSizePreference,
} from "utils/table-utils";
import { GaiaConstants } from "utils";

export interface TableFilterConfig {
  tableName: string;
  teamName: string;
  category?: string;
  dateFields: Set<string>;
  multiselectFields?: string[];
  numericFields?: string[];
  columnMappings?: Record<string, string>;
  customMatchFunctions?: Record<
    string,
    (itemValue: any, filterValue: any) => boolean
  >;
  searchPlaceholder?: string;
}

/**
 * Creates the date picker form component for PropertyFilter date operators
 * This is the UI component that users interact with to select dates
 */
const createDatePickerForm = () => {
  return ({ value, onChange, filter }: any) => {
    const handleDateChange = (dateString: string) => {
      if (dateString) {
        const date = new Date(dateString);
        onChange(date.toISOString().split("T")[0]);
      } else {
        onChange("");
      }
    };

    if (typeof filter === "undefined") {
      return (
        <FormField>
          <DatePicker
            value={value ?? ""}
            onChange={(event) => handleDateChange(event.detail.value)}
            placeholder="YYYY/MM/DD"
            locale="en-GB"
          />
        </FormField>
      );
    }

    return (
      <div className="date-form">
        <FormField>
          <DateInput
            value={value ?? ""}
            onChange={(event) => handleDateChange(event.detail.value)}
            placeholder="YYYY/MM/DD"
          />
        </FormField>
        <Calendar
          value={value ?? ""}
          onChange={(event) => handleDateChange(event.detail.value)}
          locale="en-GB"
        />
      </div>
    );
  };
};

/**
 * Shared date filter match function that normalizes dates to UTC start of day
 * This ensures accurate date-only comparisons regardless of time components
 * Used by both audit and gaiaobject tables for consistent date filtering
 */
export const matchDateFilter = (
  itemValue: unknown,
  filterValue: unknown,
  operator: string,
): boolean => {
  if (!itemValue || !filterValue || typeof filterValue !== "string")
    return false;

  const itemDate = new Date(Number(itemValue) * 1000);
  const filterDate = new Date(`${filterValue}T00:00:00.000Z`);

  // Normalize both dates to UTC start of day for accurate date-only comparison
  const itemTime = new Date(
    Date.UTC(
      itemDate.getUTCFullYear(),
      itemDate.getUTCMonth(),
      itemDate.getUTCDate(),
    ),
  ).getTime();

  const filterTime = new Date(
    Date.UTC(
      filterDate.getUTCFullYear(),
      filterDate.getUTCMonth(),
      filterDate.getUTCDate(),
    ),
  ).getTime();

  switch (operator) {
    case "=":
      return itemTime === filterTime;
    case "!=":
      return itemTime !== filterTime;
    case ">":
      return itemTime > filterTime;
    case ">=":
      return itemTime >= filterTime;
    case "<":
      return itemTime < filterTime;
    case "<=":
      return itemTime <= filterTime;
    default:
      return true;
  }
};

/**
 * Creates date filter operators for client-side filtering with match functions
 * Used by client-side tables that filter data in memory
 */
export const createDateFilterOperators = (
  matchFunction: (
    itemValue: any,
    filterValue: any,
    operator: string,
  ) => boolean,
) => {
  return ["=", "!=", "<", "<=", ">", ">="].map((operator) => ({
    operator,
    form: createDatePickerForm(),
    match: (itemValue: any, filterValue: any) =>
      matchFunction(itemValue, filterValue, operator),
  }));
};

/**
 * Creates date filter operators for server-side filtering without match functions
 * Used by server-side tables where filtering happens on the backend
 * The form component allows users to select dates via DatePicker
 */
export const createServerSideDateOperators = () => {
  return ["=", "!=", "<", "<=", ">", ">="].map((operator) => ({
    operator,
    form: createDatePickerForm(),
  }));
};

export const createMultiselectFilterProperty = (
  propertyLabel: string,
  propertyKey: string,
  uniqueValues: string[],
  multiselectMatchFunction: (
    operator: string,
  ) => (itemValue: any, filterValue: any) => boolean,
) => {
  return {
    propertyLabel: propertyLabel,
    key: propertyKey,
    groupValuesLabel: `${propertyLabel} values`,
    propertyValues: uniqueValues,
    operators: [
      {
        operator: "=",
        tokenType: "enum",
        match: multiselectMatchFunction("="),
      },
      {
        operator: "!=",
        tokenType: "enum",
        match: multiselectMatchFunction("!="),
      },
      {
        operator: ":",
        match: multiselectMatchFunction(":"),
      },
      {
        operator: "!:",
        match: multiselectMatchFunction("!:"),
      },
      {
        operator: "^",
        match: multiselectMatchFunction("^"),
      },
      {
        operator: "!^",
        match: multiselectMatchFunction("!^"),
      },
    ],
  };
};

export const createTextFilterProperty = (
  propertyLabel: string,
  propertyKey: string,
) => {
  return {
    propertyLabel: propertyLabel,
    key: propertyKey,
    groupValuesLabel: `${propertyLabel} values`,
    operators: GaiaConstants.TABLE_FILTER_OPERATORS,
  };
};

export const extractUniqueValues = (
  data: any[],
  propertyKey: string,
): string[] => {
  return Array.from(
    new Set(
      data
        .map((item) => item[propertyKey])
        .filter((value) => value != null)
        .map((value) => String(value)),
    ),
  ).sort();
};

export const generateTableFilteringProperties = (
  config: TableFilterConfig,
  rawColumnDefinitions: any[],
  data: any[],
  matchDateFilter: (
    itemValue: any,
    filterValue: any,
    operator: string,
  ) => boolean,
  multiselectMatchFunction: (
    operator: string,
  ) => (itemValue: any, filterValue: any) => boolean,
  createNumericFilteringProperty?: (label: string, key: string) => any,
): any[] => {
  if (!data.length) return [];

  const FILTER_PROPERTIES: any[] = [];
  const processedKeys = new Set<string>();

  rawColumnDefinitions.forEach((column: any) => {
    if (column.id && column.header) {
      let propertyKey = column.id;
      const propertyLabel = column.header;

      if (config.columnMappings?.[column.id]) {
        propertyKey = config.columnMappings[column.id];
      }

      if (processedKeys.has(propertyKey)) {
        return;
      }
      processedKeys.add(propertyKey);

      if (config.dateFields.has(propertyKey)) {
        FILTER_PROPERTIES.push({
          key: propertyKey,
          propertyLabel: propertyLabel,
          groupValuesLabel: `${propertyLabel} value`,
          operators: createDateFilterOperators(matchDateFilter),
        });
      } else if (
        config.numericFields?.includes(propertyKey) &&
        createNumericFilteringProperty
      ) {
        FILTER_PROPERTIES.push(
          createNumericFilteringProperty(propertyLabel, propertyKey),
        );
      } else if (config.multiselectFields?.includes(propertyKey)) {
        const uniqueValues = extractUniqueValues(data, propertyKey);
        FILTER_PROPERTIES.push(
          createMultiselectFilterProperty(
            propertyLabel,
            propertyKey,
            uniqueValues,
            multiselectMatchFunction,
          ),
        );
      } else {
        FILTER_PROPERTIES.push(
          createTextFilterProperty(propertyLabel, propertyKey),
        );
      }
    }
  });

  return FILTER_PROPERTIES;
};

export const generateTableCollectionPreferences = (
  rawColumnDefinitions: readonly any[],
  options?: {
    alwaysVisibleColumn?: string;
    nonEditableColumn?: string;
  },
): CollectionPreferencesProps => ({
  pageSizePreference,
  contentDisplayPreference: {
    title: "Select visible content",
    options: rawColumnDefinitions.map(({ id, header }) => ({
      id: id || "",
      label: `${header}`,
      alwaysVisible: id === (options?.alwaysVisibleColumn || "name"),
      editable: id !== (options?.nonEditableColumn || "id"),
    })),
  },
  cancelLabel: "Cancel",
  confirmLabel: "Confirm",
  title: "Preferences",
});

export const generateTableContentDisplay = (
  rawColumnDefinitions: readonly any[],
  defaultVisibleColumns?: string[],
) =>
  rawColumnDefinitions.map(({ id, header }) => ({
    id: id || "",
    label: `${header}`,
    visible: defaultVisibleColumns
      ? defaultVisibleColumns.includes(id || "")
      : true,
  }));

export const createTablePropertyFilterI18nStrings = (
  searchPlaceholder: string,
): PropertyFilterProps.I18nStrings => ({
  ...commonPropertyFilterI18nStrings,
  filteringPlaceholder: searchPlaceholder,
  tokenEditorAddNewTokenLabel: "Add new filter",
});

export const generateTableStorageKeys = (
  teamName: string,
  tableName: string,
  category?: string,
) => {
  const baseKey = category
    ? `${teamName}-${tableName}-${category}`
    : `${teamName}-${tableName}`;

  return {
    filters: `${baseKey}-Filters`,
    widths: `${baseKey}-Widths`,
    preferences: `${baseKey}-Preferences`,
  };
};

export const sortTableFilteringOptions = (
  filteringOptions: readonly PropertyFilterProps.FilteringOption[],
  priorityKeys?: Set<string>,
): PropertyFilterProps.FilteringOption[] => {
  if (!filteringOptions) return [];

  const groupedOptions = filteringOptions.reduce<
    Record<string, PropertyFilterProps.FilteringOption[]>
  >((acc, option) => {
    if (!acc[option.propertyKey]) {
      acc[option.propertyKey] = [];
    }
    acc[option.propertyKey].push(option);
    return acc;
  }, {});

  Object.keys(groupedOptions).forEach((key) => {
    if (!priorityKeys || priorityKeys.has(key)) {
      groupedOptions[key].sort(
        (
          a: PropertyFilterProps.FilteringOption,
          b: PropertyFilterProps.FilteringOption,
        ) => {
          if (
            !Number.isNaN(Number(a.value)) &&
            !Number.isNaN(Number(b.value))
          ) {
            return Number(a.value) - Number(b.value);
          }
          return String(a.value).localeCompare(String(b.value));
        },
      );
    }
  });

  return Object.values(groupedOptions).flat();
};
